/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** The pieces the seats block is drawn from. */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';

const STEP_SIZE = 36;

const MinusIcon: React.FC = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" aria-hidden="true"
  >
    <path d="M5 12h14" />
  </svg>
);

const PlusIcon: React.FC = () => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" aria-hidden="true"
  >
    <path d="M12 5v14" /><path d="M5 12h14" />
  </svg>
);

/**
 * One plan's counter.
 *
 * Every click counts, including the fast ones: the handler moves the draft by
 * one and never reads it back from the DOM, so holding the button does not
 * drop steps between renders.
 */
export const SeatStepper: React.FC<{
  planId: string;
  value: number;
  canRemove: boolean;
  canAdd: boolean;
  onStep: (by: number) => void;
}> = ({ planId, value, canRemove, canAdd, onStep }) => {
  const { t } = useTranslation('features/subscription');
  const button = (enabled: boolean): React.CSSProperties => ({
    width: STEP_SIZE, height: STEP_SIZE, borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-medium)', background: 'var(--surface-primary)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: enabled ? 'var(--text-primary)' : 'var(--text-tertiary-soft)',
    cursor: enabled ? 'pointer' : 'not-allowed', opacity: enabled ? 1 : 0.5,
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <UnstyledButton
        onClick={() => onStep(-1)}
        disabled={!canRemove}
        aria-label={t('v2.seats.removeOne')}
        data-testid={`plan-seats-minus-${planId}`}
        style={button(canRemove)}
      >
        <MinusIcon />
      </UnstyledButton>
      <Text
        component="span"
        data-testid={`plan-seats-${planId}`}
        style={{
          minWidth: 28, textAlign: 'center', fontFamily: 'var(--font-family-alt-2)',
          fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)',
          color: 'var(--text-primary)',
        }}
      >
        {value}
      </Text>
      <UnstyledButton
        onClick={() => onStep(1)}
        disabled={!canAdd}
        aria-label={t('v2.seats.addOne')}
        data-testid={`plan-seats-plus-${planId}`}
        style={button(canAdd)}
      >
        <PlusIcon />
      </UnstyledButton>
    </div>
  );
};

/** How a plan's draft differs from what is contracted. */
export const SeatDelta: React.FC<{ delta: number }> = ({ delta }) => {
  if (!delta) return null;
  const up = delta > 0;
  return (
    <Text
      component="span"
      style={{
        fontSize: 'var(--text-meta)', fontWeight: 'var(--font-bold)',
        color: up ? 'var(--status-success-text)' : 'var(--accent-mostarda)',
      }}
    >
      {up ? `+${delta}` : delta}
    </Text>
  );
};

/** One line of the quote: what it is, and what it costs. */
export const PreviewLine: React.FC<{
  label: string;
  value: string;
  strong?: boolean;
}> = ({ label, value, strong }) => (
  <div
    style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      gap: 'var(--space-4)',
    }}
  >
    <Text
      component="span"
      style={{
        fontSize: 'var(--text-small)', lineHeight: 1.5,
        color: 'var(--text-secondary)',
      }}
    >
      {label}
    </Text>
    <Text
      component="span"
      style={{
        fontSize: strong ? 'var(--text-sm)' : 'var(--text-small)',
        fontWeight: strong ? 'var(--font-bold)' : 'var(--font-semibold)',
        color: 'var(--text-primary)', whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Text>
  </div>
);

/** The mustard band saying a reduction is already booked. */
export const ScheduledBand: React.FC<{
  lines: Array<{ planId: string; title: string; from: number; to: number }>;
  effectiveAt: string;
  leaving: string[];
  onEdit: () => void;
  onCancel: () => void;
}> = ({ lines, effectiveAt, leaving, onEdit, onCancel }) => {
  const { t } = useTranslation('features/subscription');
  return (
    <div
      data-testid="plan-scheduled"
      style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
        padding: 'var(--space-3-5) var(--space-5)',
        borderLeft: '3px solid var(--accent-mostarda)',
        background: 'var(--surface-primary)',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      <Text
        component="span"
        style={{
          fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)',
          color: 'var(--accent-mostarda)',
        }}
      >
        {t('v2.seats.scheduledFor', { date: effectiveAt })}
      </Text>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
        {lines.map((line) => (
          <Text
            key={line.planId}
            component="span"
            style={{ fontSize: 'var(--text-meta)', color: 'var(--text-secondary)' }}
          >
            {line.title}
            {' '}
            <s>{line.from}</s>
            {' → '}
            {t('v2.seats.seatCount', { count: line.to })}
          </Text>
        ))}
      </div>
      {leaving.length > 0 && (
        <Text
          style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary-soft)' }}
        >
          {t('v2.seats.whoLeavesOnThatDate', { names: leaving.join(', ') })}
        </Text>
      )}
      <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
        <UnstyledButton
          onClick={onEdit}
          data-testid="plan-scheduled-edit"
          style={{
            background: 'none', cursor: 'pointer', fontSize: 'var(--text-small)',
            fontWeight: 'var(--font-semibold)', color: 'var(--link-text)',
            textDecoration: 'underline', textUnderlineOffset: 3,
          }}
        >
          {t('v2.seats.changeWhoLeaves')}
        </UnstyledButton>
        <UnstyledButton
          onClick={onCancel}
          data-testid="plan-scheduled-cancel"
          style={{
            background: 'none', cursor: 'pointer', fontSize: 'var(--text-small)',
            fontWeight: 'var(--font-semibold)', color: 'var(--link-text)',
            textDecoration: 'underline', textUnderlineOffset: 3,
          }}
        >
          {t('v2.seats.cancelReduction')}
        </UnstyledButton>
      </div>
    </div>
  );
};
